import React, { useEffect } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { loginRequest, clearLoginSuccess } from "../../../redux/action";
import { toast } from "react-hot-toast";
import { PrimaryButton } from "../../../components/Buttons";
// import { FormError } from "../../../components/Form/FormError";

// Icons
import { MailIcon, LockClosedIcon } from "@heroicons/react/outline";
import { SignNav } from "../../../components/Navbar/Webpages/AuthNav";
import { FormError } from "../../../components/Error";

const Login = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const { loginSuccess, loginError, loginLoading } = useSelector((state) => ({
    loginSuccess: state.ajaxStatuses.success.login,
    loginError: state.ajaxStatuses.errors.login,
    loginLoading: state.loadingIndicator.loginLoading,
  }));

  const formik = useFormik({
    initialValues: {
      usernameOrEmail: "",
      password: "",
    },
    validationSchema: yup.object({
      usernameOrEmail: yup.string().required("Enter email"),
      password: yup.string().required("Enter your password"),
    }),
    onSubmit: (values) => {
      dispatch(loginRequest(values));
    },
  });

  useEffect(() => {
    if (loginError) {
      toast.error(loginError, { duration: 3000 });
    }
  }, [loginError]);

  useEffect(() => {
    if (loginSuccess) {
      toast.success(loginSuccess, { duration: 3000 });
      history.push("/admin/dashboard");
      dispatch(clearLoginSuccess());
    }
  }, [loginSuccess, history, dispatch]);

  return (
    <div>
      <SignNav />

      <div className="min-h-[90vh] bg-white flex ">
        {/* --- LEFT SIDE: LOGIN FORM --- */}
        <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-20 xl:px-24 bg-white relative z-10">
          <div className="mx-auto w-full w-full lg:w-96">
            <div>
              <h2 className="mt-6 text-3xl font-heading font-extrabold text-gray-900">
                Admin Portal
              </h2>
              <p className="mt-2 text-sm text-gray-600 font-body">
                Please sign in to access the dashboard.
              </p>
            </div>

            <div className="mt-8">
              <form onSubmit={formik.handleSubmit} className="space-y-6">
                {/* Email Input */}
                <div>
                  <label className="block text-sm font-heading font-bold text-gray-700 mb-2">
                    Email Address
                  </label>
                  <div className="relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MailIcon
                        className="h-5 w-5 text-gray-400"
                        aria-hidden="true"
                      />
                    </div>
                    <input
                      type="text"
                      {...formik.getFieldProps("usernameOrEmail")}
                      className={`
                      block w-full pl-10 pr-3 py-3 border rounded-xl leading-5 placeholder-gray-400 
                      focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary sm:text-sm transition-all duration-200 font-body
                      ${
                        formik.errors.usernameOrEmail &&
                        formik.touched.usernameOrEmail
                          ? "border-red-300 bg-red-50 text-red-900 placeholder-red-300"
                          : "border-gray-200 bg-gray-50 focus:bg-white text-gray-900"
                      }
                    `}
                      placeholder="admin@primeglobal.com"
                    />
                  </div>
                  {formik.errors.usernameOrEmail &&
                    formik.touched.usernameOrEmail && (
                      <div className="mt-1">
                        <FormError message={formik.errors.usernameOrEmail} />
                      </div>
                    )}
                </div>

                {/* Password Input */}
                <div>
                  <label className="block text-sm font-heading font-bold text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <LockClosedIcon
                        className="h-5 w-5 text-gray-400"
                        aria-hidden="true"
                      />
                    </div>
                    <input
                      type="password"
                      {...formik.getFieldProps("password")}
                      className={`
                      block w-full pl-10 pr-3 py-3 border rounded-xl leading-5 placeholder-gray-400 
                      focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary sm:text-sm transition-all duration-200 font-body
                      ${
                        formik.errors.password && formik.touched.password
                          ? "border-red-300 bg-red-50 text-red-900 placeholder-red-300"
                          : "border-gray-200 bg-gray-50 focus:bg-white text-gray-900"
                      }
                    `}
                      placeholder="••••••••"
                    />
                  </div>
                  {formik.errors.password && formik.touched.password && (
                    <div className="mt-1">
                      <FormError message={formik.errors.password} />
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <div className="pt-2">
                  <PrimaryButton
                    text={loginLoading ? "Authenticating..." : "Secure Login"}
                    action={formik.handleSubmit}
                    loading={loginLoading}
                    disabled={!formik.isValid || loginLoading}
                    className="w-full flex justify-center py-3 px-4 border border-transparent rounded-full shadow-sm text-sm font-medium text-white bg-primary hover:bg-primaryDark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-300"
                  />
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* --- RIGHT SIDE: BRAND IMAGE OVERLAY --- */}
        <div className="hidden lg:block relative w-0 flex-1 overflow-hidden">
          {/* 1. Image */}
          <img
            className="absolute inset-0 h-full w-full object-cover"
            src="https://images.unsplash.com/photo-1497366216548-37526070297c?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80"
            alt="Office background"
          />

          {/* 2. THE BRAND OVERLAY (Matching other pages) */}
          <div className="absolute inset-0 bg-primary/90 mix-blend-multiply z-10"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent z-10"></div>

          {/* 3. Decorative Content (Optional) */}
          <div className="absolute bottom-0 left-0 p-20 z-20 text-white">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center mb-6">
              <svg
                className="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                ></path>
              </svg>
            </div>
            <h3 className="font-heading font-bold text-4xl mb-2">
              Secure Administration
            </h3>
            <p className="font-body text-blue-100 max-w-md text-lg">
              Manage users, monitor investments, and oversee platform
              performance from one central hub.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
